import { AfterViewInit, Component, ElementRef, OnInit, ViewChild } from '@angular/core'
import { FormsModule, ReactiveFormsModule, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms'
import { ActivatedRoute, Router } from '@angular/router'
import { Objects } from '../../../../server/src/shared-with-client/object-definitions'
import { ParseService } from '../parse.service'
import { UtmLoginStorageKey } from './login'
import { CommonModule } from '@angular/common'
import { IndeterminateButtonLoadingDirective } from '../shared/directives/indeterminate-button-loading/indeterminate-button-loading.directive'
import { MatButtonModule } from '@angular/material/button'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatIconModule } from '@angular/material/icon'
import { MatInputModule } from '@angular/material/input'

@Component({
    selector: 'app-login',
    templateUrl: './login.component.html',
    styleUrls: ['./../app.component.scss', './login.component.scss'],
    imports: [
        CommonModule,
        FormsModule,
        IndeterminateButtonLoadingDirective,
        MatButtonModule,
        MatFormFieldModule,
        MatIconModule,
        MatInputModule,
        ReactiveFormsModule,
    ],
})
export class LoginComponent implements AfterViewInit, OnInit {
    errorMessage: string | undefined
    isLoggingIn = false
    forgotPassword = false
    forgotMessage: string | undefined
    isResettingPassword = false
    isResetEmailSent = false

    // We check for required in onLoginButtonClicked()
    // Validators.required causes the placeholders to turn red too early
    loginFormControls = {
        email: new TrimmedFormControl('', { validators: [Validators.email] }),
        password: new UntypedFormControl(''),
    }
    loginForm: UntypedFormGroup = new UntypedFormGroup(this.loginFormControls)

    resetPasswordFormControl = {
        email: new TrimmedFormControl('', { validators: [Validators.email] }),
    }
    resetPasswordForm: UntypedFormGroup = new UntypedFormGroup(this.resetPasswordFormControl)

    @ViewChild('emailLoginElement') emailLoginElement?: ElementRef
    @ViewChild('emailResetElement') emailResetElement?: ElementRef

    constructor(
        private parseService: ParseService,
        private router: Router,
        public element: ElementRef<HTMLElement>,
        private route: ActivatedRoute
    ) {}

    ngOnInit() {
        this.route.queryParams.subscribe((params) => {
            this.forgotPassword = params['reset-password']
        })
    }

    private getFinalRedirectUrl(redirectUrl: string): string {
        if (!redirectUrl) {
            return ''
        }

        if (redirectUrl.startsWith('/login')) {
            const nestedRedirect = redirectUrl.split('/login?')[1]
            const includesNestedRedirect = !!nestedRedirect
            if (includesNestedRedirect) {
                const nestedRedirectParsed = nestedRedirect.split('redirectUrl=')[1] ?? ''
                return this.getFinalRedirectUrl(decodeURIComponent(nestedRedirectParsed))
            } else {
                return ''
            }
        } else {
            return redirectUrl
        }
    }

    ngAfterViewInit(): void {
        this.focusElement('loginEmail')

        try {
            const queryParamsSnapshot = this.route.snapshot.queryParams

            if (queryParamsSnapshot) {
                const key: UtmLoginStorageKey = 'wise-pelican/utm-login'
                const existingParamString = localStorage.getItem(key)
                const utmParams: Objects.Establishment.UtmParameters = existingParamString ? JSON.parse(existingParamString) : {}

                if (queryParamsSnapshot['utm_campaign']) {
                    utmParams.campaign = queryParamsSnapshot['utm_campaign']
                }
                if (queryParamsSnapshot['utm_content']) {
                    utmParams.content = queryParamsSnapshot['utm_content']
                }
                if (queryParamsSnapshot['utm_medium']) {
                    utmParams.medium = queryParamsSnapshot['utm_medium']
                }
                if (queryParamsSnapshot['utm_source']) {
                    utmParams.source = queryParamsSnapshot['utm_source']
                }
                if (queryParamsSnapshot['utm_term']) {
                    utmParams.term = queryParamsSnapshot['utm_term']
                }
                if (queryParamsSnapshot['utm_device']) {
                    utmParams.device = queryParamsSnapshot['utm_device']
                }

                if (Object.keys(utmParams).length > 0) {
                    const value = JSON.stringify(utmParams)
                    localStorage.setItem(key, value)
                }
            }
        } catch (err) {}
    }

    getControlErrorMessage(control: UntypedFormControl) {
        const errors = control.errors

        if (!errors) {
            return ''
        }

        if (errors['required']) {
            return 'This field is required'
        }

        if (errors['email']) {
            return 'Please enter a valid email address'
        }

        return 'Invalid value'
    }

    async onLoginButtonClicked() {
        const email = this.loginFormControls.email.value?.trim() || ''
        const password = this.loginFormControls.password.value || ''
        const trimmedPassword = password.trim()
        if (!email) {
            this.loginFormControls.email.setErrors({ required: true })
        }

        if (!trimmedPassword) {
            if (trimmedPassword !== password) {
                this.loginFormControls.password.setValue(null)
            }
            this.loginFormControls.password.setErrors({ required: true })
        }

        if (!this.loginForm.valid) {
            return
        }

        try {
            this.isLoggingIn = true

            const user = await this.parseService.logIn(email, trimmedPassword, { updateDefaultEstablishment: true })
            let roles = await this.parseService.roleService.find.toPromise({ user })
            console.log('Roles', roles)

            if (roles.length === 0) {
                await new Promise((resolve) => {
                    setTimeout(resolve, 1000)
                })
                console.log('2nd roles query attempt')
                this.parseService.reportUIWarning(new Error('2nd Login Attempt'), 'LoginComponent.onLoginButtonClicked', '')
                this.parseService.roleService.clearRoles()
                this.parseService.roleService.find.resetAll()
                roles = await this.parseService.roleService.find.toPromise({ user })
                console.log('Roles 2', roles)

                if (roles.length === 0) {
                    await new Promise((resolve) => {
                        setTimeout(resolve, 4000)
                    })
                    console.log('3rd roles query attempt')
                    this.parseService.reportUIWarning(new Error('3rd Login Attempt'), 'LoginComponent.onLoginButtonClicked', '')
                    this.parseService.roleService.clearRoles()
                    this.parseService.roleService.find.resetAll()
                    roles = await this.parseService.roleService.find.toPromise({ user })
                    console.log('Roles 3', roles)
                }
            }

            console.log('Finding Redirect')
            const redirectUrl = this.getFinalRedirectUrl(this.route.snapshot.queryParamMap.get('redirectUrl') ?? '')
            console.log('Found Redirect', redirectUrl)

            const useRouter = roles.length > 0

            if (redirectUrl && redirectUrl !== '/login') {
                console.log('Go to redirect')
                if (useRouter) {
                    await this.router.navigateByUrl(redirectUrl)
                } else {
                    window.open(location.origin + redirectUrl, '_parent')
                }
            } else if (roles.length === 1 && (roles[0]?.get('name') === 'ProFulfillment Customer' || roles[0]?.get('name') === 'User')) {
                if (useRouter) {
                    await this.router.navigate(['/tracking'])
                } else {
                    window.open(location.origin + '/tracking', '_parent')
                }
                console.log('Go to Tracking')
            } else {
                if (useRouter) {
                    await this.router.navigate(['/products'])
                } else {
                    window.open(location.origin + '/products', '_parent')
                }
                console.log('Go to Products')
            }
        } catch (err) {
            console.log(err)
            const ignoreReportingErrorsList = ['Invalid username/password.', 'User Account Disabled']
            if (ignoreReportingErrorsList.indexOf(err?.message) === -1) {
                this.parseService.reportUIError(err, 'LoginComponent.onLoginButtonClicked()', JSON.stringify({ email }))
            }
            this.isLoggingIn = false
            this.errorMessage = this.parseService.getErrorMessage(err) || 'Error Logging In. Please Reset Password'
        }
    }

    async onResetPasswordButtonClick() {
        this.forgotMessage = ''
        const email: string = this.resetPasswordFormControl.email.value?.trim() || ''
        const emailTrimmedLowerCase = email.trim().toLowerCase()
        if (!emailTrimmedLowerCase) {
            this.resetPasswordFormControl.email.setErrors({ required: true })
        }

        if (!this.resetPasswordForm.valid) {
            return
        }

        try {
            this.isResettingPassword = true

            await this.parseService.logOut()
            await this.parseService.requestPasswordReset(emailTrimmedLowerCase)
            this.resetPasswordFormControl.email.setValue(null)
            this.forgotMessage = 'An email has been sent to you with instructions on how to recover your account'
            this.isResetEmailSent = true
        } catch (err) {
            const message = this.parseService.getErrorMessage(err)
            const warningMessageList = ['No User Found']

            const isWarning = warningMessageList.indexOf(message) > -1
            if (isWarning) {
                this.parseService.reportUIWarning(err, 'LoginComponent.resetPassword()', '')
            } else {
                this.parseService.reportUIError(err, 'LoginComponent.resetPassword()', '')
            }

            let forgotMessage = 'Error Submitting Request. Please Contact Wise Pelican'
            if (message) {
                forgotMessage = message
                if (message === 'No User Found') {
                    forgotMessage = "We couldn't find an account with that email"
                }
            }
            this.forgotMessage = forgotMessage
            this.isResetEmailSent = false
        }
        this.isResettingPassword = false
    }

    removeResetPasswordQueryParam() {
        this.router.navigate([], {
            relativeTo: this.route,
            queryParams: { 'reset-password': null }, // Removes the 'resetPassword' query param
            queryParamsHandling: 'merge',
        })
    }

    onReturnToLoginFooterClick() {
        this.forgotPassword = false
        this.errorMessage = ''
        this.forgotMessage = ''
        this.loginForm.reset()
        this.removeResetPasswordQueryParam()
        this.isResetEmailSent = false
        this.focusElement('loginEmail')
    }

    onResetPasswordFooterClick() {
        this.forgotPassword = true
        this.errorMessage = ''
        this.forgotMessage = ''
        this.resetPasswordForm.reset()
        this.focusElement('resetEmail')
    }

    private focusElement(elementName: 'loginEmail' | 'resetEmail') {
        setTimeout(() => {
            const element: ElementRef | undefined =
                elementName === 'loginEmail' ? this.emailLoginElement : elementName === 'resetEmail' ? this.emailResetElement : undefined

            if (element) {
                element.nativeElement.focus()
            }
        }, 200)
    }
}

export class TrimmedFormControl extends UntypedFormControl {
    private _value: string | undefined

    // TODO Can the getter be removed?
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    //@ts-ignore
    get value(): string | undefined {
        return this._value
    }

    set value(value: string | undefined) {
        this._value = value ? value.trim() : value
    }
}
